<script lang="ts" setup>
import { reactive } from 'vue';
import useVuelidate from '@vuelidate/core';
import { required, email, minLength, helpers, numeric, decimal } from '@vuelidate/validators';
import BaseCheckbox from '@/components/base/BaseCheckbox.vue';
import BaseRadio from '@/components/base/BaseRadio.vue';
import BaseNumber from '@/components/base/BaseNumber.vue';
import BaseToggle from '@/components/base/BaseToggle.vue';

const formDataTargetGroup = reactive({
  targetGroup: "mentallyDisabledPeople",
});

const formData = reactive({
  name: '',
  email: '',
  password: '',
  role: '',
  access: false,
  gender: 'female',
  age: 0,
  salary: 0,
  toggle: false,
  mentallyDisabledPeople: true,
  peopleWithAddictionDisorders:false
});


const customRequired = helpers.withMessage('This field is required.', required);
const customMinLength = (length: number) =>
  helpers.withMessage(`Minimum length is ${length} characters.`, minLength(length));
const customEmail = helpers.withMessage('Please provide a valid email address.', email);

const rules = {
  name: { required: customRequired },
  email: { required: customRequired, email: customEmail },
  password: { required: customRequired, minLength: customMinLength(6) },
  role: { required: customRequired },
  age: { required: customRequired },
  salary: { required: customRequired },
  access: { required: (value: any) => value === true || 'You must accept access' },
  gender: { required: (value: any) => value === true || 'You must accept access' },
};

const v$ = useVuelidate(rules, formData);

const handleSubmit = () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }
};
</script>

<template>
  <div class="container">
    <div class="element-box mt-4">
      <form @submit.prevent="handleSubmit" class="needs-validation" novalidate>
      <!-- Name Field -->
      <div class="form-group">
        <label for="name">Name</label>
        <input
          type="text"
          class="form-control"
          :class="{ 'is-invalid': v$.name.$error }"
          id="name"
          v-model="v$.name.$model"
        />
        <span v-for="err in v$.name.$errors" :key="err.$uid" class="text-danger">
          {{ err.$message }}
        </span>
      </div>
  
      <!-- Email Field -->
      <div class="form-group">
        <label for="email">Email</label>
        <input
          type="email"
          class="form-control"
          :class="{ 'is-invalid': v$.email.$error }"
          id="email"
          v-model="v$.email.$model"
        />
        <span v-for="err in v$.email.$errors" :key="err.$uid" class="text-danger">
          {{ err.$message }}
        </span>
      </div>
  
      <!-- Password Field -->
      <div class="form-group">
        <label for="password">Password</label>
        <input
          type="password"
          class="form-control"
          :class="{ 'is-invalid': v$.password.$error }"
          id="password"
          v-model="v$.password.$model"
        />
        <span v-for="err in v$.password.$errors" :key="err.$uid" class="text-danger">
          {{ err.$message }}
        </span>
      </div>
  
      <!-- Dropdown Field -->
      <div class="form-group">
        <label for="role">Role</label>
        <select
          id="role"
          class="form-select"
          :class="{ 'is-invalid': v$.role.$error }"
          v-model="v$.role.$model"
        >
          <option value="">Select your role</option>
          <option value="admin">Admin</option>
          <option value="user">User</option>
          <option value="guest">Guest</option>
        </select>
        <span v-for="err in v$.role.$errors" :key="err.$uid" class="text-danger">
          {{ err.$message }}
        </span>
      </div>
  
      <BaseNumber 
        label="Age"
        :decimalPlaces="2"
        :min="-1000000"
        v-model="formData.age"
        :validation="v$.age"
        :messages="v$.age.$errors.map((err) => err.$message).join(', ')"
      />
      <BaseNumber 
        label="Curreny"
        :isCurrency="true"
        v-model="formData.salary"
        :validation="v$.salary"
        :messages="v$.salary.$errors.map((err) => err.$message).join(', ')"
      />

     <div>
      <h4>Checkboxes</h4>
      <div class="d-flex mb-4" style="gap: 8px;">
        <BaseCheckbox
          v-model="formData.access"
          :validation="v$.access"
          label="Access"
          :required="true"
          :labelPosition="'right'"
          :classInput="'me-2'"
          :classLabel="'mb-0'"
          :messages="v$.access.$errors.map((err) => err.$message).join(', ')"
        />

        <BaseCheckbox
          v-model="formData.access"
          :validation="v$.access"
          label="Checkbox disabled"
          :disabled="true"
          :required="true"
          :labelPosition="'right'"
          :classInput="'me-2'"
          :classLabel="'mb-0'"
          :messages="v$.access.$errors.map((err) => err.$message).join(', ')"
        />
      </div>
     </div>
  
      <h4>Radios</h4>
      <div class="row gx-2">
        <BaseRadio
          v-model="formData.gender"
          :validation="v$.gender"
          :disabled="true"
          label="Male"
          name="gender"
          id="male"
          :required="true"
          :messages="v$.gender.$errors.map((err: any) => err.$message).join(', ')"
          classFormGroup="col-auto"
        />
        <BaseRadio
          v-model="formData.gender"
          :validation="v$.gender"
          label="Female"
          name="gender"
          id="female"
          :required="true"
          :messages="v$.gender.$errors.map((err: any) => err.$message).join(', ')"
          classFormGroup="col-auto"
        />
        
      </div>

      <div class="mb-4">
        <h4>Radio buttons custom</h4>
        <BaseRadio
            v-model="formDataTargetGroup.targetGroup"
            labelPosition="right"
            name="Zielgruppe"
            classFormGroup="mb-0 custom-radio"
            label="Psychisch behinderte Menschen"
            id="mentallyDisabledPeople"/>
        <BaseRadio
            v-model="formDataTargetGroup.targetGroup"
            labelPosition="right"
            name="Zielgruppe"
            :disabled="true"
            classFormGroup="mb-0 custom-radio"
            label="Menschen mit schwerwiegenden, andauernden Abhängigkeitserkrankungen"
            id="peopleWithAddictionDisorders"/>
      </div>

      <div class="mb-4">
        <h4>Toggles</h4>
        <BaseToggle v-model="formData.toggle" label="Toggle label" />
      </div>

      <div class="d-flex mb-3" style="gap: 8px;">
          <div class="popover-custom btn btn-primary">
            <div class="popover-content position-top">
              Title popover
              <h6 class="text-danger mt-2">test</h6>
            </div>
          popover hover top
        </div>

        <div class="popover-custom btn btn-primary">
            <div class="popover-content position-bottom">
              Title popover
              <h6 class="text-danger mt-2">test</h6>
            </div>
          popover hover bottom
        </div>

        <div class="popover-custom btn btn-link">
            <div class="popover-content position-left">
              Title popover
              <h6 class="text-danger mt-2">test</h6>
            </div>
          popover hover left
        </div>

        <div class="popover-custom btn btn-outline-primary">
            <div class="popover-content position-right">
              Title popover
              <h6 class="text-danger mt-2">test</h6>
            </div>
          popover hover right
        </div>
      </div>
      <!-- Submit Button -->
      <button type="submit" class="btn btn-primary">Submit</button>
      </form>
    </div>
  </div>
</template>

<style scoped>
.text-danger {
  font-size: 0.9rem;
}
</style>
